  <template>
    <div class="package-registration-create container">
      <!-- Header -->
      <div class="d-flex align-items-center my-8">
        <button class="btn btn-icon btn-light mr-4" @click="goBack">
          <i class="fas fa-arrow-left"></i>
        </button>
        <div>
          <h4 class="text-dark fw-bold mb-1">Đăng ký Gói Dịch vụ Mới</h4>
          <p class="text-muted m-0">Chọn khách hàng và gói dịch vụ để đăng ký</p>
        </div>
      </div>

      <div class="section-card mb-6">
        <h5 class="section-title mb-4">Chọn khách hàng</h5>

        <div class="d-flex mb-4">
          <div class="position-relative flex-grow-1 mr-3">
            <i class="fas fa-search search-field-icon"></i>
            <input
              v-model="searchKeyword"
              type="text"
              class="form-control search-field"
              placeholder="Nhập tên hoặc số điện thoại khách hàng..."
              @input="onSearchCustomer"
              @keyup.enter="searchCustomerNow"
            />
          </div>
          <b-form-select
            v-model="customerSource"
            class="source-select mr-2"
            @change="searchCustomerNow"
          >
            <option value="all">Tất cả</option>
            <option value="his">His</option>
            <option value="diab">Diab</option>
          </b-form-select>
          <button class="btn btn-success" @click="searchCustomerNow" :disabled="loadingCustomers">
            <i class="fas fa-search mr-1"></i>Tìm kiếm
          </button>
          <button class="btn btn-outline-success ml-2" @click="openAddCustomerModal">
            <i class="fas fa-user-plus mr-1"></i>Thêm khách hàng
          </button>
        </div>

        <b-overlay :show="loadingCustomers" rounded="sm">
          <div v-if="customers.length === 0 && !loadingCustomers" class="text-center py-6">
            <i class="fas fa-users fa-2x text-muted mb-3 d-block"></i>
            <p class="text-muted">Không tìm thấy khách hàng nào.</p>
          </div>

          <ul v-else class="customer-list">
            <li
              v-for="customer in customers"
              :key="customer.id"
              class="customer-list-item"
              :class="{ 'customer-list-item--selected': selectedCustomerId === customer.id }"
              @click="selectedCustomerId = customer.id"
            >
              <div class="customer-radio mr-3">
                <span
                  class="radio-dot"
                  :class="{ 'radio-dot--active': selectedCustomerId === customer.id }"
                ></span>
              </div>
              <div class="customer-list-info">
                <span class="customer-name text-dark">{{ customer.fullName || [customer.firstName, customer.middleName, customer.lastName].filter(Boolean).join(' ') || '—' }}</span>
                <div class="customer-meta text-muted">
                  <span v-if="customer.phoneMobile || customer.phoneNumber"><i class="fas fa-phone-alt mr-1"></i>{{ customer.phoneMobile || customer.phoneNumber }}</span>
                  <span v-if="(customer.phoneMobile || customer.phoneNumber) && customer.email" class="mx-2">·</span>
                  <span v-if="customer.email"><i class="fas fa-envelope mr-1"></i>{{ customer.email }}</span>
                  <span v-if="(customer.phoneMobile || customer.email) && customer.addressLine1" class="mx-2">·</span>
                  <span v-if="customer.addressLine1"><i class="fas fa-map-marker-alt mr-1"></i>{{ customer.addressLine1 }}</span>
                </div>
              </div>
              <i
                v-if="selectedCustomerId === customer.id"
                class="fas fa-check-circle text-success customer-check-icon"
              ></i>
            </li>
          </ul>
        </b-overlay>
      </div>

      <div class="section-card mb-6">
        <h5 class="section-title mb-4">Chọn gói dịch vụ</h5>

        <div class="position-relative mb-4">
          <i class="fas fa-search search-field-icon"></i>
          <input
            v-model="packageSearchText"
            type="text"
            class="form-control search-field"
            placeholder="Nhập tên gói để tìm kiếm..."
          />
        </div>

        <b-overlay :show="loadingPackages" rounded="sm">
          <div v-if="filteredPackages.length === 0 && !loadingPackages" class="text-center py-6">
            <i class="fas fa-box-open fa-2x text-muted mb-3 d-block"></i>
            <p class="text-muted">Không tìm thấy gói dịch vụ nào.</p>
          </div>

          <div v-else class="row">
            <div
              v-for="pkg in filteredPackages"
              :key="pkg.id"
              class="col-4 mb-4"
            >
              <div
                class="package-card"
                :class="{ 'package-card--selected': selectedPackageId === pkg.id }"
                @click="selectedPackageId = pkg.id"
              >
                <div class="d-flex align-items-start">
                  <div class="customer-radio mr-3">
                    <span
                      class="radio-dot"
                      :class="{ 'radio-dot--active': selectedPackageId === pkg.id }"
                    ></span>
                  </div>
                  <div class="flex-grow-1">
                    <div class="package-name fw-bold text-dark">{{ pkg.name }}</div>
                    <p class="text-muted mb-2 package-desc">{{ pkg.description }}</p>
                    <div class="d-flex align-items-center flex-wrap mb-2">
                      <span class="package-price text-success fw-bold">
                        {{ formatPrice(pkg.salePrice) }} VNĐ
                      </span>
                    </div>
                    <div class="package-stats text-muted">
                      {{ pkg.medicineCount }} thuốc • {{ pkg.servicePackageCount }} CT • {{ pkg.serviceCount }} DV
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </b-overlay>
      </div>

      <!-- Section 3: Thông tin giá & Voucher -->
      <div v-if="selectedPackageId" class="section-card mb-6">
        <h5 class="section-title mb-5">Thông tin giá & Voucher</h5>

        <!-- Voucher input -->
        <div class="mb-4">
          <label class="form-label text-dark fw-bold">Mã giảm giá (Tùy chọn)</label>
          <div class="d-flex">
            <input
              v-model="voucherCode"
              type="text"
              class="form-control mr-3"
              placeholder="Nhập mã voucher"
              style="border-radius: 8px; height: 42px;"
            />
            <button class="btn btn-outline-secondary" @click="applyVoucher" :disabled="loadingVoucher" style="white-space: nowrap;">
              <span v-if="loadingVoucher" class="spinner-border spinner-border-sm mr-1"></span>
              Áp dụng
            </button>
          </div>
          <div v-if="voucherNotification" class="mt-2" :class="voucherError ? 'text-danger' : 'text-success'">
            <i :class="voucherError ? 'fas fa-times-circle mr-1' : 'fas fa-check-circle mr-1'"></i>
            {{ voucherNotification }}
          </div>
        </div>

        <!-- Price summary -->
        <div class="price-summary mb-4">
          <div class="d-flex justify-content-between py-2">
            <span class="text-muted">Giá gói:</span>
            <span class="text-dark">{{ formatPrice(selectedPackagePrice) }} VNĐ</span>
          </div>
          <div v-if="discountAmount > 0" class="d-flex justify-content-between py-2">
            <span class="text-muted">Giảm giá:</span>
            <span class="text-danger">-{{ formatPrice(discountAmount) }} VNĐ</span>
          </div>
          <div class="d-flex justify-content-between py-2 border-top" style="border-color: #e4e6ef !important;">
            <span class="fw-bold text-dark">Số tiền cần thanh toán:</span>
            <span class="fw-bold text-success" style="font-size: 1.1rem;">{{ formatPrice(totalPayment) }} VNĐ</span>
          </div>
        </div>

        <!-- Payment amount -->
        <div class="mb-4">
          <label class="form-label text-dark fw-bold">Số tiền khách hàng đã thanh toán <span class="text-danger">*</span></label>
          <input
            v-model.number="paidAmount"
            type="number"
            class="form-control"
            :placeholder="String(totalPayment)"
            style="border-radius: 8px; height: 42px;"
          />
          <div v-if="paidAmount >= totalPayment && paidAmount > 0" class="mt-2 text-success">
            <i class="fas fa-check mr-1"></i>Đã thanh toán đủ
          </div>
          <div v-else-if="paidAmount > 0 && paidAmount < totalPayment" class="mt-2 text-warning">
            <i class="fas fa-exclamation-triangle mr-1"></i>Còn thiếu {{ formatPrice(totalPayment - paidAmount) }} VNĐ
          </div>
        </div>

        <!-- Registration date -->
        <div class="mb-2">
          <basic-date-picker
            label="Ngày đăng ký"
            placeholder="DD/MM/YYYY"
            name="registrationDate"
            format="DD/MM/YYYY"
            :value.sync="registrationDate"
          />
        </div>
      </div>

      <!-- Footer actions -->
      <div class="d-flex justify-content-end mb-8">
        <button class="btn btn-outline-secondary mr-3" @click="goBack">Hủy</button>
        <button
          class="btn btn-success"
          :disabled="!selectedCustomerId || !selectedPackageId || paidAmount === null || submitting"
          @click="submitRegistration"
        >
          <span v-if="submitting" class="spinner-border spinner-border-sm mr-2"></span>
          <i v-else class="fas fa-check mr-2"></i>Xác nhận đăng ký
        </button>
      </div>

      <!-- Add Customer Modal -->
      <b-modal
        v-model="showAddCustomerModal"
        title="Đăng ký khách hàng mới"
        size="xl"
        scrollable
        hide-footer
        header-class="add-customer-modal-header"
        body-class="add-customer-modal-body"
      >
        <form @submit.prevent="submitNewCustomer">
          <!-- Thông tin khách hàng (chỉ hiển thị các trường cần thiết) -->
          <div class="form-section">
            <h6 class="form-section__title">
              <i class="fas fa-user mr-2 text-primary"></i>Thông tin cá nhân
            </h6>
            <div class="row">
              <div class="col-md-12 mb-3">
                <label class="form-label">Họ và tên <span class="text-danger">*</span></label>
                <input v-model="newCustomer.fullNameInput" type="text" class="form-control modal-input"/>
              </div>
              <div class="col-md-4 mb-3">
                <basic-date-picker
                  label="Ngày sinh"
                  placeholder="DD/MM/YYYY"
                  name="dateOfBirth"
                  format="DD/MM/YYYY"
                  :value.sync="newCustomer.dateOfBirth"
                />
              </div>
              <div class="col-md-4 mb-3">
                <basic-select
                  label="Giới tính"
                  placeholder="-- Chọn --"
                  name="gender"
                  :options="genderOptions"
                  track-by="value"
                  value-label="text"
                  :value.sync="newCustomer.gender"
                  @input="newCustomer.gender = $event"
                />
              </div>
              <div class="col-md-4 mb-3">
                <label class="form-label">Số điện thoại</label>
                <input v-model="newCustomer.phoneMobile" type="text" maxlength="10" class="form-control modal-input" />
              </div>
            </div>
          </div>

          <!-- Modal Footer Actions -->
          <div class="d-flex justify-content-end pt-3 border-top">
            <button type="button" class="btn btn-outline-secondary mr-3" @click="showAddCustomerModal = false">
              Hủy
            </button>
          <button type="button" class="btn btn-success mr-3" :disabled="submittingCustomer" @click="openImportModal">
              <span v-if="submittingCustomer" class="spinner-border spinner-border-sm mr-2"></span>
              <span class='mr-2'><inline-svg src="/media/svg/icons/Neolex/Basic/upload-cloud.svg" /></span>Import
            </button>
            <button type="submit" class="btn btn-success" :disabled="submittingCustomer">
              <span v-if="submittingCustomer" class="spinner-border spinner-border-sm mr-2"></span>
              <i v-else class="fas fa-save mr-2"></i>Lưu khách hàng
            </button>
          </div>
        </form>
      </b-modal>

      <!-- Import Customer Modal -->
      <FormCustomerImport @import-success="onImportSuccess" />

    </div>
  </template>

  <script>
  import HisApiService from '@/core/services/his.api.service.js';
  import FormCustomerImport from './FormCustomerImport.vue';

  const hisApi = new HisApiService();

  export default {
    name: 'PackageRegistrationCreate',
    components: { FormCustomerImport },
    data() {
      return {
        packageSearchText: '',
        loadingCustomers: false,
        loadingPackages: false,
        customers: [],
        packages: [],
        selectedCustomerId: null,
        selectedPackageId: null,
        searchKeyword: '',
        customerSource: 'all',
        searchDebounceTimer: null,
        genderOptions: [
          { value: 'M', text: 'Nam' },
          { value: 'F', text: 'Nữ' },
        ],
        voucherCode: '',
        discountAmount: 0,
        paidAmount: null,
        loadingVoucher: false,
        voucherNotification: '',
        voucherError: false,
        registrationDate: new Date().toISOString().slice(0, 10),
        submitting: false,
        showAddCustomerModal: false,
        submittingCustomer: false,
        newCustomer: this.getEmptyCustomer(),
      };
    },
    computed: {
      selectedPackageObj() {
        if (!this.selectedPackageId) return null;
        return this.packages.find((p) => p.id === this.selectedPackageId) || null;
      },
      selectedPackagePrice() {
        const pkg = this.selectedPackageObj;
        if (!pkg) return 0;
        return pkg.salePrice || 0;
      },
      totalPayment() {
        return Math.max(0, this.selectedPackagePrice - this.discountAmount);
      },
      filteredPackages() {
        const q = (this.packageSearchText || '').trim().toLowerCase();
        if (!q) return this.packages;
        return this.packages.filter((p) =>
          (p.name || '').toLowerCase().includes(q) ||
          (p.description || '').toLowerCase().includes(q)
        );
      },
    },
    watch: {
      totalPayment(val) {
        // Tự động gán số tiền cần thanh toán vào ô đã thanh toán
        this.paidAmount = val;
      },
      selectedPackageId() {
        // Reset voucher state khi đổi gói
        this.discountAmount = 0;
        this.voucherNotification = '';
        this.voucherError = false;
      },
      'newCustomer.patientId'(val) {
        // Tự động gán patientId vào hisId
        this.newCustomer.hisId = val;
      },
    },
    mounted() {
      this.loadCustomers();
      this.loadPackages();
    },
    beforeDestroy() {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
    },
    methods: {
      splitFullName(fullName) {
        const parts = (fullName || '').trim().split(/\s+/);
        if (parts.length === 0 || (parts.length === 1 && parts[0] === '')) {
          return { firstName: '', middleName: '', lastName: '' };
        }
        if (parts.length === 1) {
          return { firstName: parts[0], middleName: parts[0], lastName: parts[0] };
        }
        const firstName = parts[0];
        const lastName = parts[parts.length - 1];
        const middleName = parts.slice(1, -1).join(' ');
        return { firstName, middleName, lastName };
      },

      getEmptyCustomer() {
        return {
          code: '',
          fullNameInput: '',
          firstName: '',
          middleName: '',
          lastName: '',
          dateOfBirth: '',
          gender: '',
          ssn: '',
          nationalId: '',
          email: '',
          phoneHome: '',
          phoneMobile: '',
          phoneWork: '',
          addressLine1: '',
          addressLine2: '',
          city: '',
          state: '',
          country: '',
          postalCode: '',
          passportNumber: '',
          driversLicense: '',
          maidenName: '',
          mothersMaidenName: '',
          fathersName: '',
          emergencyContactName: '',
          emergencyContactRelationship: '',
          emergencyContactPhone: '',
          nextOfKinName: '',
          nextOfKinRelationship: '',
          nextOfKinPhone: '',
          guardianName: '',
          guardianRelationship: '',
          guardianPhone: '',
          encryptionKeyId: 0,
          encryptionVersion: null,
          dataQualityScore: null,
          verificationStatus: '',
          status: 1,
          lastUpdatedProgram: '',
          patientId: '',
          hisId: '',
        };
      },

      openAddCustomerModal() {
        this.newCustomer = this.getEmptyCustomer();
        this.showAddCustomerModal = true;
      },

      openImportModal() {
        this.$bvModal.show('modal-customer-import');
      },

      async onImportSuccess() {
        // Reload danh sách khách hàng sau khi import xong
        await this.loadCustomers();
      },

      async submitNewCustomer() {
        // Tách họ và tên từ fullNameInput
        const { firstName, middleName, lastName } = this.splitFullName(this.newCustomer.fullNameInput);

        // Validate required fields
        if (!firstName && !lastName) {
          this.$toastr?.e?.({ title: 'Lỗi', msg: 'Vui lòng nhập họ và tên khách hàng' });
          return;
        }

        try {
          this.submittingCustomer = true;

          const toEpoch = (dateStr) => {
            if (!dateStr || dateStr === '0001-01-01T00:00:00') return null;
            let normalized = dateStr;
            if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
              const [d, m, y] = dateStr.split('/');
              normalized = `${y}-${m}-${d}`;
            }
            const ms = new Date(normalized).getTime();
            return isNaN(ms) ? null : Math.floor(ms / 1000);
          };
          const normalizeGender = (g) => {
            if (!g) return null;
            if (g === 'M') return 'Nam';
            if (g === 'F') return 'Nữ';
            return g;
          };

          const payload = {
            Code: this.newCustomer.code || null,
            HisId: this.newCustomer.hisId || null,
            PatientId: this.newCustomer.patientId || null,
            FirstName: firstName || null,
            MiddleName: middleName || null,
            LastName: lastName || null,
            DateOfBirth: toEpoch(this.newCustomer.dateOfBirth),
            Gender: normalizeGender(this.newCustomer.gender),
            Ssn: this.newCustomer.ssn || null,
            NationalId: this.newCustomer.nationalId || null,
            Email: this.newCustomer.email || null,
            PhoneHome: this.newCustomer.phoneHome || null,
            PhoneMobile: this.newCustomer.phoneMobile || null,
            PhoneWork: this.newCustomer.phoneWork || null,
            AddressLine1: this.newCustomer.addressLine1 || null,
            AddressLine2: this.newCustomer.addressLine2 || null,
            City: this.newCustomer.city || null,
            State: this.newCustomer.state || null,
            Country: this.newCustomer.country || null,
            PostalCode: this.newCustomer.postalCode || null,
            PassportNumber: this.newCustomer.passportNumber || null,
            DriversLicense: this.newCustomer.driversLicense || null,
            MaidenName: this.newCustomer.maidenName || null,
            MothersMaidenName: this.newCustomer.mothersMaidenName || null,
            FathersName: this.newCustomer.fathersName || null,
            EmergencyContactName: this.newCustomer.emergencyContactName || null,
            EmergencyContactRelationship: this.newCustomer.emergencyContactRelationship || null,
            EmergencyContactPhone: this.newCustomer.emergencyContactPhone || null,
            NextOfKinName: this.newCustomer.nextOfKinName || null,
            NextOfKinRelationship: this.newCustomer.nextOfKinRelationship || null,
            NextOfKinPhone: this.newCustomer.nextOfKinPhone || null,
            GuardianName: this.newCustomer.guardianName || null,
            GuardianRelationship: this.newCustomer.guardianRelationship || null,
            GuardianPhone: this.newCustomer.guardianPhone || null,
            EncryptionKeyId: this.newCustomer.encryptionKeyId || 0,
            EncryptionVersion: this.newCustomer.encryptionVersion || null,
            DataQualityScore: this.newCustomer.dataQualityScore || null,
            VerificationStatus: this.newCustomer.verificationStatus || null,
            Status: this.newCustomer.status ?? 1,
            LastUpdatedProgram: this.newCustomer.lastUpdatedProgram || null,
          };

          await this.$api.post('AccountHis', payload);

          this.$toastr?.s?.({
            title: 'Thành công',
            msg: `Đã thêm khách hàng "${(this.newCustomer.fullNameInput || '').trim() || `${firstName} ${lastName}`}"`,
          });

          this.showAddCustomerModal = false;

          // Reload customer list to include the new one
          this.searchKeyword = this.newCustomer.phoneMobile || firstName || '';
          await this.loadCustomers();
        } catch (error) {
          console.error('Lỗi thêm khách hàng:', error);
          this.$toastr?.e?.({
            title: 'Lỗi',
            msg: 'Không thể thêm khách hàng. Vui lòng thử lại.',
          });
        } finally {
          this.submittingCustomer = false;
        }
      },

      goBack() {
        this.$router.push({ name: 'package_registration' });
      },

      onSearchCustomer() {
        if (this.searchDebounceTimer) {
          clearTimeout(this.searchDebounceTimer);
        }
        this.searchDebounceTimer = setTimeout(() => {
          this.searchCustomerNow();
        }, 2000);
      },

      searchCustomerNow() {
        if (this.searchDebounceTimer) {
          clearTimeout(this.searchDebounceTimer);
        }
        this.selectedCustomerId = null;
        this.loadCustomers();
      },

      async loadCustomers() {
        try {
          this.loadingCustomers = true;
          const keyword = (this.searchKeyword || '').trim();
          // Cho phép nhập số thường (0xxx) hoặc đã có +84 sẵn — tự chuẩn hoá về +84 trước khi gọi API
          const isPhone = keyword ? /^[0-9+]+$/.test(keyword) : false;
          const toPlus84 = (phone) => {
            if (!phone) return phone;
            if (phone.startsWith('+84')) return phone;
            const digits = phone.replace(/\D/g, '');
            if (digits.startsWith('0')) return '+84' + digits.slice(1);
            if (digits.startsWith('84')) return '+' + digits;
            return '+84' + digits;
          };
          const normalizedKeyword = isPhone ? toPlus84(keyword) : keyword;
          const paramsPatpii = {};
          if (keyword) {
            if (isPhone) paramsPatpii.phoneMobile = normalizedKeyword;
            else paramsPatpii.fullName = keyword;
          }

          const extractList = (res) => {
            const raw = res?.data ?? res;
            return Array.isArray(raw) ? raw : (raw?.data ?? raw?.items ?? []);
          };

          const loadDiab = async () => {
            const res = await this.$api.get('/AccountHis/search-mobile-patients', {
              params: keyword ? { keyword: normalizedKeyword } : {},
            });
            return extractList(res).map((item) => ({
              ...item,
              phoneMobile: item.phoneMobile || item.phoneNumber,
              _source: 'diab',
            }));
          };
          const loadHis = async () => {
            const res = await hisApi.get('/api/patpiidata?latestOnly=1', { params: paramsPatpii });
            return extractList(res).map((item) => ({ ...item, _source: 'his' }));
          };

          let list = [];

          if (this.customerSource === 'diab') {
            list = await loadDiab();
          } else if (this.customerSource === 'his') {
            list = await loadHis();
          } else {
            // "all": HIS trước, không ra thì fallback Diab (accounts mobile)
            try {
              list = await loadHis();
            } catch (hisErr) {
              console.warn('HIS patpiidata lỗi, fallback sang Diab:', hisErr);
              list = [];
            }
            if (list.length === 0) {
              try {
                list = await loadDiab();
              } catch (diabErr) {
                console.error('Diab search-mobile-patients cũng lỗi:', diabErr);
                list = [];
              }
            }
          }

          this.customers = list;
        } catch (error) {
          console.error('Lỗi tải danh sách khách hàng:', error);
          const sourceLabel = this.customerSource === 'diab' ? 'Diab' : this.customerSource === 'his' ? 'HIS' : 'HIS / Diab';
          this.$toastr?.e?.({ title: 'Lỗi', msg: `Không thể tải danh sách khách hàng từ ${sourceLabel}` });
        } finally {
          this.loadingCustomers = false;
        }
      },

      async applyVoucher() {
        if (!this.voucherCode.trim()) {
          this.$toastr?.e?.({ title: 'Lỗi', msg: 'Vui lòng nhập mã voucher' });
          return;
        }
        if (!this.selectedCustomerId) {
          this.$toastr?.e?.({ title: 'Lỗi', msg: 'Vui lòng chọn khách hàng trước khi áp dụng mã' });
          return;
        }
        if (!this.selectedPackageId) {
          this.$toastr?.e?.({ title: 'Lỗi', msg: 'Vui lòng chọn gói dịch vụ trước' });
          return;
        }

        const customer = this.customers.find((c) => c.id === this.selectedCustomerId);
        if (!customer) return;

        try {
          this.loadingVoucher = true;
          this.voucherNotification = '';
          this.voucherError = false;

          const toEpoch = (dateStr) => {
            if (!dateStr || dateStr === '0001-01-01T00:00:00') return null;
            let normalized = dateStr;
            if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
              const [d, m, y] = dateStr.split('/');
              normalized = `${y}-${m}-${d}`;
            }
            const ms = new Date(normalized).getTime();
            return isNaN(ms) ? null : Math.floor(ms / 1000);
          };
          const normalizeGender = (g) => {
            if (!g) return null;
            if (g === 'M') return 'Nam';
            if (g === 'F') return 'Nữ';
            return g;
          };
          const isDiab = customer._source === 'diab';

          const accountHis = {
            Code: customer.code || null,
            HisId: isDiab ? null : (customer.id || null),
            PatientId: customer.patientId || null,
            FirstName: customer.firstName || null,
            MiddleName: customer.middleName || null,
            LastName: customer.lastName || null,
            DateOfBirth: isDiab ? null : toEpoch(customer.dateOfBirth),
            Gender: normalizeGender(customer.gender),
            Ssn: customer.ssn || null,
            NationalId: customer.nationalId || null,
            Email: customer.email || null,
            PhoneHome: customer.phoneHome || null,
            PhoneMobile: customer.phoneMobile || null,
            PhoneWork: customer.phoneWork || null,
            AddressLine1: customer.addressLine1 || customer.address || null,
            AddressLine2: customer.addressLine2 || null,
            City: customer.city || null,
            State: customer.state || null,
            Country: customer.country || null,
            PostalCode: customer.postalCode || null,
            PassportNumber: customer.passportNumber || null,
            DriversLicense: customer.driversLicense || null,
            MaidenName: customer.maidenName || null,
            MothersMaidenName: customer.mothersMaidenName || null,
            FathersName: customer.fathersName || null,
            EmergencyContactName: customer.emergencyContactName || null,
            EmergencyContactRelationship: customer.emergencyContactRelationship || null,
            EmergencyContactPhone: customer.emergencyContactPhone || null,
            NextOfKinName: customer.nextOfKinName || null,
            NextOfKinRelationship: customer.nextOfKinRelationship || null,
            NextOfKinPhone: customer.nextOfKinPhone || null,
            GuardianName: customer.guardianName || null,
            GuardianRelationship: customer.guardianRelationship || null,
            GuardianPhone: customer.guardianPhone || null,
            EncryptionKeyId: customer.encryptionKeyId || 0,
            EncryptionVersion: customer.encryptionVersion ?? null,
            LastAccessedAt: toEpoch(customer.lastAccessedAt),
            AccessCount: customer.accessCount ?? null,
            DataQualityScore: customer.dataQualityScore ?? null,
            VerificationStatus: customer.verificationStatus || null,
            VerifiedBy: customer.verifiedBy ?? null,
            VerifiedAt: toEpoch(customer.verifiedAt),
            CreatedAt: toEpoch(customer.createdAt),
            LastUpdatedAt: toEpoch(customer.lastUpdatedAt),
            Status: customer.status ?? 1,
            CreatedBy: customer.createdBy ?? null,
            LastUpdatedBy: customer.lastUpdatedBy ?? null,
            LastUpdatedProgram: customer.lastUpdatedProgram || null,
          };

          const res = await this.$api.post('BundleVouchers/applyVoucher', accountHis, {
            params: {
              packageId: this.selectedPackageId,
              code: this.voucherCode.trim(),
            },
          });
          const price = res?.item1 ?? 0;
          const saleOff = res?.item2 ?? 0;
          const notification = res?.item3 ?? '';

          if (price === 0 && saleOff === 0) {
            this.$toastr?.e?.({ title: 'Không hợp lệ', msg: notification || 'Mã voucher không hợp lệ hoặc không áp dụng cho gói này' });
          } else {
            // Thành công
            this.discountAmount = saleOff;
            this.$toastr?.s?.({ title: 'Thành công', msg: notification || 'Mã voucher hợp lệ' });
          }
        } catch (error) {
        
        } finally {
          this.loadingVoucher = false;
        }
      },

      async loadPackages() {
        try {
          this.loadingPackages = true;
          const res = await this.$api.get('SalePackage?status=1&size=1000');
          const raw = res?.data ?? res;
          if (Array.isArray(raw)) {
            this.packages = raw;
          } else if (Array.isArray(raw?.items)) {
            this.packages = raw.items;
          } else if (Array.isArray(raw?.data)) {
            this.packages = raw.data;
          } else {
            this.packages = [];
          }
        } catch (error) {
          console.error('Lỗi tải danh sách gói dịch vụ:', error);
          this.$toastr?.e?.({ title: 'Lỗi', msg: 'Không thể tải danh sách gói dịch vụ' });
        } finally {
          this.loadingPackages = false;
        }
      },

      async submitRegistration() {
        if (this.submitting) return;
        const customer = this.customers.find((c) => c.id === this.selectedCustomerId);
        const pkg = this.packages.find((p) => p.id === this.selectedPackageId);
        if (!customer || !pkg) return;

        // Kiểm tra số tiền khách thanh toán phải khớp đúng với số tiền cần thanh toán
        if (Number(this.paidAmount) !== Number(this.totalPayment)) {
          this.$toastr?.e?.({
            title: 'Lỗi thanh toán',
            msg: `Số tiền thanh toán phải là ${this.formatPrice(this.totalPayment)} VNĐ`,
          });
          return;
        }

        try {
          this.submitting = true;

          const toEpoch = (dateStr) => {
            if (!dateStr || dateStr === '0001-01-01T00:00:00') return null;
            let normalized = dateStr;
            if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
              const [day, month, year] = dateStr.split('/');
              normalized = `${year}-${month}-${day}`;
            }
            const ms = new Date(normalized).getTime();
            return isNaN(ms) ? null : Math.floor(ms / 1000);
          };
          const normalizeGender = (g) => {
            if (!g) return null;
            if (g === 'M') return 'Nam';
            if (g === 'F') return 'Nữ';
            return g;
          };
          const isDiab = customer._source === 'diab';

          const accountHis = {
            Code: customer.code || null,
            HisId: isDiab ? null : (customer.id || null),
            PatientId: customer.patientId || null,
            FirstName: customer.firstName || null,
            MiddleName: customer.middleName || null,
            LastName: customer.lastName || null,
            DateOfBirth: isDiab ? null : toEpoch(customer.dateOfBirth),
            Gender: normalizeGender(customer.gender),
            Ssn: customer.ssn || null,
            NationalId: customer.nationalId || null,
            Email: customer.email || null,
            PhoneHome: customer.phoneHome || null,
            PhoneMobile: customer.phoneMobile || customer.phoneNumber || null,
            PhoneWork: customer.phoneWork || null,
            AddressLine1: customer.addressLine1 || customer.address || null,
            AddressLine2: customer.addressLine2 || null,
            City: customer.city || null,
            State: customer.state || null,
            Country: customer.country || null,
            PostalCode: customer.postalCode || null,
            PassportNumber: customer.passportNumber || null,
            DriversLicense: customer.driversLicense || null,
            MaidenName: customer.maidenName || null,
            MothersMaidenName: customer.mothersMaidenName || null,
            FathersName: customer.fathersName || null,
            EmergencyContactName: customer.emergencyContactName || null,
            EmergencyContactRelationship: customer.emergencyContactRelationship || null,
            EmergencyContactPhone: customer.emergencyContactPhone || null,
            NextOfKinName: customer.nextOfKinName || null,
            NextOfKinRelationship: customer.nextOfKinRelationship || null,
            NextOfKinPhone: customer.nextOfKinPhone || null,
            GuardianName: customer.guardianName || null,
            GuardianRelationship: customer.guardianRelationship || null,
            GuardianPhone: customer.guardianPhone || null,
            EncryptionKeyId: customer.encryptionKeyId || 0,
            EncryptionVersion: customer.encryptionVersion ?? null,
            LastAccessedAt: toEpoch(customer.lastAccessedAt),
            AccessCount: customer.accessCount ?? null,
            DataQualityScore: customer.dataQualityScore ?? null,
            VerificationStatus: customer.verificationStatus || null,
            VerifiedBy: customer.verifiedBy ?? null,
            VerifiedAt: toEpoch(customer.verifiedAt),
            CreatedAt: toEpoch(customer.createdAt),
            LastUpdatedAt: toEpoch(customer.lastUpdatedAt),
            Status: customer.status ?? 1,
            CreatedBy: customer.createdBy ?? null,
            LastUpdatedBy: customer.lastUpdatedBy ?? null,
            LastUpdatedProgram: customer.lastUpdatedProgram || null,
            PriceBuy: this.paidAmount || 0,
            StartDate: toEpoch(this.registrationDate || new Date().toISOString().slice(0, 10)),
          };

          await this.$api.post('UserBundleOrders', accountHis, {
            params: {
              salePackageId: this.selectedPackageId,
              voucherCode: this.voucherCode.trim(),
            },
          });
          this.$toastr?.s?.({
            title: 'Thành công',
            msg: `Đã đăng ký "${pkg.name}" cho "${customer.fullName}"`,
          });
          this.goBack();
        } catch (error) {
          console.error('Lỗi đăng ký gói:', error);
          this.$toastr?.e?.({
            title: 'Lỗi',
            msg: 'Đăng ký gói dịch vụ thất bại. Vui lòng thử lại.',
          });
        } finally {
          this.submitting = false;
        }
      },

      formatPrice(val) {
        if (!val && val !== 0) return '0';
        return new Intl.NumberFormat('vi-VN').format(val);
      },
    },
  };
  </script>

  <style scoped lang="scss">
  .package-registration-create {
    padding: 0;
  }

  .source-select {
    width: 110px;
    flex-shrink: 0;
    height: 42px;
    border-radius: 8px;
    border: 1px solid #e1e5e9;
    font-size: 0.9rem;
    font-weight: 600;
  }

  .btn-icon {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    border: 1px solid #e4e6ef;
    background: #fff;
    transition: all 0.2s;

    &:hover {
      background: #f3f6f9;
      border-color: #d3d3da;
    }
  }

  .section-card {
    background: #f8f9fb;
    border-radius: 14px;
    padding: 24px 28px;
  }

  .section-title {
    font-weight: 700;
    color: #181c32;
    font-size: 1rem;
  }

  /* Customer list */
  .customer-list {
    list-style: none;
    padding: 0;
    margin: 0;
    border: 1.5px solid #e4e6ef;
    border-radius: 12px;
    max-height: 260px;
    overflow-y: auto;
    overflow-x: hidden;

    /* Custom scrollbar */
    &::-webkit-scrollbar {
      width: 6px;
    }
    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 0 12px 12px 0;
    }
    &::-webkit-scrollbar-thumb {
      background: #c8ccd8;
      border-radius: 3px;
      &:hover {
        background: #a1a5b7;
      }
    }
  }

  .customer-list-item {
    display: flex;
    align-items: center;
    padding: 12px 18px;
    background: #fff;
    cursor: pointer;
    transition: background 0.15s ease;
    border-bottom: 1px solid #f0f0f5;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background: #f8f9fb;
    }

    &--selected {
      background: #f0fdf4;

      .customer-name {
        color: #1a7f4b;
      }
    }
  }

  .customer-list-info {
    flex: 1;
    min-width: 0;
  }

  .customer-meta {
    font-size: 0.82rem;
    margin-top: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .customer-check-icon {
    font-size: 1.1rem;
    flex-shrink: 0;
    margin-left: 8px;
  }

  /* Customer card */
  .customer-card {
    background: #fff;
    border: 1.5px solid #e4e6ef;
    border-radius: 12px;
    padding: 16px 20px;
    margin-bottom: 12px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      border-color: #b5b5c3;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
    }

    &--selected {
      border-color: #50cd89;
      background: #f0fdf4;
      box-shadow: 0 0 0 1px #50cd89;
    }
  }

  .customer-name {
    font-size: 0.95rem;
    font-weight: 600;
  }

  .customer-info {
    font-size: 0.85rem;
  }

  /* Search fields */
  .search-field {
    padding-left: 38px;
    border-radius: 8px;
    border: 1px solid #e1e5e9;
    height: 42px;
    font-size: 0.9rem;
  }

  .search-field-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #a1a5b7;
    z-index: 5;
    font-size: 0.85rem;
  }

  /* Radio dot */
  .customer-radio {
    padding-top: 3px;
  }

  .radio-dot {
    display: inline-block;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 2px solid #ccc;
    position: relative;
    transition: all 0.2s;

    &--active {
      border-color: #50cd89;

      &::after {
        content: '';
        position: absolute;
        top: 3px;
        left: 3px;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: #50cd89;
      }
    }
  }

  /* Package card */
  .package-card {
    background: #fff;
    border: 1.5px solid #e4e6ef;
    border-radius: 12px;
    padding: 18px 20px;
    cursor: pointer;
    transition: all 0.2s ease;
    height: 100%;

    &:hover {
      border-color: #b5b5c3;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
    }

    &--selected {
      border-color: #50cd89;
      background: #f0fdf4;
      box-shadow: 0 0 0 1px #50cd89;
    }
  }

  .package-name {
    font-size: 0.95rem;
  }

  .package-desc {
    font-size: 0.83rem;
    line-height: 1.4;
  }

  .package-price {
    font-size: 1.05rem;
  }

  .package-stats {
    font-size: 0.82rem;
  }

  /* Add Customer Modal */
  ::v-deep .add-customer-modal-header {
    background: #f8f9fb;
    border-bottom: 1px solid #e4e6ef;
    padding: 16px 24px;

    .modal-title {
      font-weight: 700;
      color: #181c32;
      font-size: 1.05rem;
    }
  }

  ::v-deep .add-customer-modal-body {
    padding: 24px 28px;
  }

  .form-section {
    margin-bottom: 24px;
    padding-bottom: 20px;
    border-bottom: 1px solid #f0f0f5;

    &:last-of-type {
      border-bottom: none;
      margin-bottom: 0;
      padding-bottom: 0;
    }

    &__title {
      font-weight: 700;
      font-size: 0.92rem;
      color: #181c32;
      margin-bottom: 16px;
      display: flex;
      align-items: center;
    }
  }

  .form-label {
    font-size: 0.84rem;
    font-weight: 600;
    color: #5e6278;
    margin-bottom: 4px;
  }

  .modal-input {
    border-radius: 8px;
    border: 1px solid #e1e5e9;
    height: 38px;
    font-size: 0.88rem;
    transition: border-color 0.2s;

    &:focus {
      border-color: #50cd89;
      box-shadow: 0 0 0 0.2rem rgba(80, 205, 137, 0.15);
    }
  }
  </style>
